<template>
  <div data-testid="lightbox-vm18-container">
    <ui-modal
      id="modal-vm18"
      :shown="showModal"
      class="modal-vm18"
      no-close
      data-testid="lightbox-vm18-modal"
    >
      <template #header>
        <slot name="header">
          <div
            class="d-flex flex-column justify-content-center align-items-center text-center container"
            data-testid="lightbox-vm18-header"
          >
            <div class="modal-vm18__logo-container" data-testid="lightbox-vm18-logo">
              <svg-vm18-vivaincontri v-if="isVivaincontri" />
              <svg-vm18-skokka v-else />
            </div>

            <h2
              id="modal-vm18__title"
              class="text-center font-weight-bolder text-2xl mt-3"
              data-testid="lightbox-vm18-title"
            >
              {{ i18n.pleaseRead }}
            </h2>
          </div>
        </slot>
      </template>
      <template #body>
        <slot name="body">
          <div class="container d-flex flex-column justify-content-center align-items-center" data-testid="lightbox-vm18-body">
            <p
              id="modal-vm18__text"
              class="text-center max-w-320"
              v-html="i18n.eighteenYearOld"
              data-testid="lightbox-vm18-text"
            ></p>
            <hr class="my-1 border-0" />
            <p
              id="modal-vm18__terms-and-conditions"
              class="text-center m-0 p-0"
              v-text="i18n.termsAndConditions"
              data-testid="lightbox-vm18-terms-text"
            ></p>
            <ui-button
              class="m-0 p-0"
              :href="tcUrl"
              :data-hreh="tcUrl"
              button-type="blank"
              normal-case
              no-box-shadow
              :label="i18n.tac"
              data-testid="lightbox-vm18-terms-link"
            />
          </div>
        </slot>
      </template>
      <template #footer>
        <slot name="footer">
          <div class="container d-flex flex-column justify-content-center align-items-center" data-testid="lightbox-vm18-footer">
            <ui-button
              class="m-0"
              appearance="primary"
              :label="i18n.accept"
              @click="accept"
              data-testid="lightbox-vm18-accept-button"
            />
            <ui-button
              class="m-0"
              href="https://www.google.com"
              rel="noopener nofollow"
              button-type="blank"
              normal-case
              no-box-shadow
              :label="i18n.refuse"
              data-testid="lightbox-vm18-refuse-button"
            />
          </div>
        </slot>
      </template>
    </ui-modal>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import Url from 'domurl'

  import { privacyCookieName, getLightboxName } from '../utils'

  const COOKIE_NAME = 'adult_cookie'
  export default {
    components: {
      UiModal: () =>
        import(/* webpackChunkName: "chunk-ui-modal", webpackMode: "eager"  */ './ui/ui-modal.vue'),
      UiButton: () => import(/* webpackChunkName: "chunk-ui-button" */ './ui/ui-button.vue'),
      SvgVm18Skokka: () =>
        import(/* webpackChunkName: "chunk-svg-vm18-skokka" */ './svg/vm18-skokka.vue'),
      SvgVm18Vivaincontri: () =>
        import(/* webpackChunkName: "chunk-svg-vm18-vivaincontri" */ './svg/vm18-vivaincontri.vue'),
    },
    props: {
      privacyCookie: {
        type: Boolean,
        required: true,
        default: false,
      },
      tcUrl: {
        type: String,
        required: true,
      },
      isVivaincontri: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        lightboxName: null,
        adultCookie: false,
        i18n: {
          pleaseRead: gettext('Please read the following warning before continuing'),
          eighteenYearOld: String(
            gettext(
              ' <b>I am over 18 years old</b>  and I accept the viewing of explicit texts and images intended for an <b>adult audience</b><br>'
            )
          ).replace('<br>', '.<br>'),
          accept: gettext('Accept'),
          refuse: gettext('Decline'),
          termsAndConditions: gettext('I have read and accept the'),
          tac: gettext('Terms and Conditions'),
        },
      }
    },
    computed: {
      ...mapGetters({
        canUseCookies: 'ui/device/canUseCookies',
      }),
      showModal() {
        return this.$store.getters['ui/lightboxManager/lightboxIsShown'](this.lightboxName)
      },
    },
    watch: {
      privacyCookie() {
        if (this.privacyCookie && this.adultCookie) {
          this.$cookies.set(COOKIE_NAME, '1', '1d', '/', locations.cookie_domain)
        }
      },
    },
    mounted() {
      this.lightboxName = getLightboxName({ el: this.$el })

      /**
       Manage the display status of cookies based on the presence of cookies and the user's country when privacy cookies are enabled.
       In countries where privacy cookies are required, both the adult cookie and the privacy cookie need to be checked.
       In countries where privacy cookies are not required, only the adult cookie needs to be checked.
       */

      const requiresPrivacyCookie = locations.privacy_cookie_country || false
      const hasCookie = !!this.$cookies.get(COOKIE_NAME) || false
      const hasPrivacyCookie = !!this.$cookies.get(privacyCookieName) || false
      const url = new Url(window.location.href)
      const isLightboxQueryPresent = url.query.lightbox === 'true'
      const shouldRemoveFromQueue = requiresPrivacyCookie
        ? hasCookie && hasPrivacyCookie
        : hasCookie

      if (isLightboxQueryPresent) {
        document.body.classList.add('blurredbg')
      }

      if (shouldRemoveFromQueue && !isLightboxQueryPresent) {
        const hasBlurredClass = document.body.classList.contains('blurredbg')

        this.$store.dispatch('ui/lightboxManager/removeAndHide', this.lightboxName)

        if (hasBlurredClass) {
          document.body.classList.remove('blurredbg')
        }
      }
    },
    methods: {
      accept() {
        const hasBlurredClass = document.body.classList.contains('blurredbg')

        if (this.canUseCookies) {
          this.$cookies.set(COOKIE_NAME, '1', '1d', '/', locations.cookie_domain)
        } else {
          this.adultCookie = true
        }
        this.$store.dispatch('ui/lightboxManager/hide')

        if (hasBlurredClass) {
          document.body.classList.remove('blurredbg')
        }
      },
    },
  }
</script>

<style>
  #modal-vm18__title,
  #modal-vm18__text,
  #modal-vm18__terms-and-conditions {
    color: var(--color-body);
  }

  .modal-vm18__logo-container {
    min-height: 82px;
    min-width: 91px;
  }
</style>
